import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { Input } from '@/src/shadComponents/ui/input';
import { Separator } from '@/src/shadComponents/ui/separator';
import { Alert, AlertDescription } from '@/src/shadComponents/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Checkbox } from '@/src/shadComponents/ui/checkbox';

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [feedback, setFeedback] = useState<string | null>(null);
    const { signin, setPersist, persist, signInGoogle } = useAuth();

    // @ts-ignore
    let from = location.state?.from?.pathname || '/';

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const username = formData.get('username') as string;
        const password = formData.get('password') as string;

        try {
            const authenticated = await signin(username, password);

            if (authenticated) {
                navigate(from, { replace: true });
            } else {
                setFeedback('Log in failed, try again.');
            }
        } catch (e) {
            setFeedback('Log in failed, try again.');
        }
    }

    async function handleGoogleResponse(credential: string) {
        const authenticated = await signInGoogle(credential);
        if (authenticated) {
            navigate(from, { replace: true });
        } else {
            setFeedback('Google account does not exist in Photostat.');
        }
    }

    useEffect(() => {
        const script = document.createElement('script');

        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;

        document.body.appendChild(script);

        setTimeout(() => {
            // @ts-ignore
            google.accounts.id.initialize({
                client_id:
                    '475304023009-uaqeorfcb9d09jill84neov7bde5ve3m.apps.googleusercontent.com',
                // @ts-ignore
                callback: (jwt: string) => handleGoogleResponse(jwt.credential),
            });

            // @ts-ignore
            google.accounts.id.renderButton(
                document.getElementById('buttonDiv'),
                { theme: 'outline', size: '100' },
            );

            // @ts-ignore
            google.accounts.id.prompt();
        }, 1000);
        return () => {
            document.body.removeChild(script);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // @ts-ignore
        localStorage.setItem('persist', persist);
    }, [persist]);

    return (
        <div className="h-screen flex items-center justify-center bg-background">
            <Card className="w-[90%] max-w-md shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-2xl font-bold tracking-tight">
                        Welcome to Photostat
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                        Sign in to your account to continue.
                    </p>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {feedback && (
                            <Alert variant="destructive">
                                <div className="flex items-center">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription className="ml-2">
                                        {feedback}
                                    </AlertDescription>
                                </div>
                            </Alert>
                        )}

                        <div className="space-y-2">
                            <label
                                htmlFor="username"
                                className="text-sm font-medium"
                            >
                                Username
                            </label>
                            <Input
                                id="username"
                                name="username"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <label
                                htmlFor="password"
                                className="text-sm font-medium"
                            >
                                Password
                            </label>
                            <Input
                                id="password"
                                name="password"
                                type="password"
                                required
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="persist"
                                checked={persist}
                                onCheckedChange={(checked) =>
                                    setPersist(!!checked)
                                }
                            />
                            <label
                                htmlFor="persist"
                                className="text-sm font-medium cursor-pointer"
                            >
                                Trust this device for 30 days
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-primary text-primary-foreground py-2 rounded-md shadow-md hover:bg-primary/90 text-center"
                        >
                            Log In
                        </button>
                    </form>

                    <Separator className="my-4" />
                    <div
                        id="buttonDiv"
                        className="flex justify-center"
                    ></div>
                </CardContent>
            </Card>
        </div>
    );
}
